import {
  TrainingAudienceFilter,
  TrainingDurationMin,
  TrainingEducationLevel,
  TrainingStatus,
} from "../../services/formacion/formacion.types";

/**
 * Alcance "todos los colegios". El backend lo escribe como `school_id` NULL;
 * en la UI viaja como 0 porque un Select de antd no distingue null de vacío.
 */
export const ALL_SCHOOLS = 0;
export const ALL_SCHOOLS_LABEL = "Todos los colegios";
/** Campo del formulario del modal; NO viaja en el payload del training. */
export const TARGET_SCHOOL_FIELD = "target_school_id";

export interface SchoolOption {
  id: number;
  name: string;
  // Present when the option comes straight from the `useSchools()` cache
  // (SCRUM-1098 D-3 filter); absent in hand-built fixtures, which are then
  // treated as an already-selectable root school.
  deleted_at?: Date | null;
  is_active?: boolean;
  parent_id?: number | null;
}

// SCRUM-1098 D-3: the audience picker only offers active, non-deleted ROOT
// schools — a campus is reached through its root, never selected on its own.
export function filterSelectableSchools(
  schools: SchoolOption[]
): SchoolOption[] {
  return schools
    .filter(
      (school) =>
        school.deleted_at == null &&
        school.is_active !== false &&
        school.parent_id == null
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

const AUDIENCE_SUMMARY_MAX_NAMES = 3;

// SCRUM-1098: `selectedIds` is the raw form value, which may still carry the
// `ALL_SCHOOLS` UI sentinel or be empty — both mean "all schools".
export function formatSchoolAudienceSummary(
  selectedIds: number[],
  schools: SchoolOption[]
): string {
  if (selectedIds.length === 0 || selectedIds.includes(ALL_SCHOOLS)) {
    return ALL_SCHOOLS_LABEL;
  }
  const names = selectedIds.map(
    (id) => schools.find((school) => school.id === id)?.name ?? `#${id}`
  );
  const shown = names.slice(0, AUDIENCE_SUMMARY_MAX_NAMES);
  const remaining = names.length - shown.length;
  return remaining > 0 ? `${shown.join(", ")} +${remaining}` : shown.join(", ");
}

export const EDUCATION_LEVEL_LABELS: Record<TrainingEducationLevel, string> = {
  KINDER: "Preescolar",
  PRIMARIA: "Primaria",
};

export const DURATION_OPTIONS: { value: TrainingDurationMin; label: string }[] =
  [
    { value: 45, label: "45 minutos" },
    { value: 60, label: "60 minutos" },
    { value: 90, label: "90 minutos" },
  ];

export interface TimezoneOption {
  value: string;
  label: string;
}

export const DEFAULT_TIMEZONE = "America/Mexico_City";

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: "America/Mexico_City", label: "(GMT-06:00) Ciudad de México" },
  { value: "America/Cancun", label: "(GMT-05:00) Cancún" },
  { value: "America/Hermosillo", label: "(GMT-07:00) Hermosillo" },
  { value: "America/Tijuana", label: "(GMT-08:00) Tijuana" },
];

export const TIMEZONE_LABELS: Record<string, string> = TIMEZONE_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);

export const AUDIENCE_FILTER_OPTIONS: {
  value: TrainingAudienceFilter | "ALL";
  label: string;
}[] = [
  { value: "ALL", label: "Todas" },
  { value: "TEACHERS", label: "Profesores" },
  { value: "COORDINATORS", label: "Coordinadores" },
  { value: "BOTH", label: "Ambos" },
];

export const STATUS_FILTER_OPTIONS: {
  value: TrainingStatus | "ALL";
  label: string;
}[] = [
  { value: "ALL", label: "Todos" },
  { value: "SCHEDULED", label: "Programada" },
  { value: "LIVE", label: "En vivo" },
  { value: "COMPLETED", label: "Realizada" },
];

export function formatAudiencePhrase(row: {
  audience_teachers: boolean;
  audience_coordinators: boolean;
}): string {
  if (row.audience_teachers && row.audience_coordinators) {
    return "Profesores y coordinadores";
  }
  if (row.audience_coordinators) return "Coordinadores";
  return "Profesores";
}
